/**
 * @module UploadReceiptModalProbe
 * @purpose Probes transport (`http_upload` vs `staged`) whenever a zip file
 *   is picked, before any bytes move (spec FR-staged / PRP AC2).
 * @depends services/evidence-containers (probeReceiptTransport)
 * @dependedOnBy use-upload-receipt-modal
 *
 * @rules
 * - State: local useState; re-probes whenever the selected file changes
 * - Errors: sets `probeError`; never throws from hook body
 */

import { useCallback, useEffect, useState } from 'react'
import { probeReceiptTransport } from '../../../services/evidence-containers'
import type { ProbeReceiptResponse } from '../../../types/evidence-containers'

export interface UseTransportProbeResult {
  transportProbe: ProbeReceiptResponse | null
  probing: boolean
  probeError: string | null
  retryProbe: () => void
}

/**
 * @behaviour Fires a probe request whenever `file` changes to a non-null
 *   value; clears the prior probe result when `file` is cleared.
 * @errors `retryProbe` re-runs the same probe after a transient failure.
 */
export function useTransportProbe(
  caseId: string,
  requestId: string,
  file: File | null
): UseTransportProbeResult {
  const [transportProbe, setTransportProbe] = useState<ProbeReceiptResponse | null>(null)
  const [probing, setProbing] = useState(false)
  const [probeError, setProbeError] = useState<string | null>(null)

  const runProbe = useCallback(
    async (target: File) => {
      setProbing(true)
      setProbeError(null)
      try {
        const result = await probeReceiptTransport(caseId, requestId, {
          fileName: target.name,
          sizeBytes: target.size,
        })
        setTransportProbe(result)
      } catch (err) {
        setProbeError(err instanceof Error ? err.message : 'Failed to determine upload transport')
        setTransportProbe(null)
      } finally {
        setProbing(false)
      }
    },
    [caseId, requestId]
  )

  useEffect(() => {
    if (!file) {
      setTransportProbe(null)
      setProbeError(null)
      return
    }
    void runProbe(file)
  }, [file, runProbe])

  const retryProbe = useCallback(() => {
    if (file) void runProbe(file)
  }, [file, runProbe])

  return { transportProbe, probing, probeError, retryProbe }
}
